import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  Save,
  Plus,
  Trash2,
  ArrowLeft,
  Loader2,
  RotateCcw,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Cashflow Settings — single-form editor for the user's persisted cashflow
 * scenario. Mirrors the planner's input shape exactly (accounts, bills,
 * expectedIncome, reserves, planningDate, majorIncomeDate).
 *
 * Storage pattern: full-document upsert (PUT /api/cashflow/scenario), same
 * approach as financial_plan — keeps the editor / planner contract
 * trivially symmetric.
 */

const ACCOUNT_TYPES = [
  { value: 'personal_checking', label: 'Personal Checking' },
  { value: 'business_checking', label: 'Business Checking' },
  { value: 'hysa', label: 'HYSA / Savings' },
  { value: 'credit_card', label: 'Credit Card (Debt)' },
];

const DEBT_PRIORITIES = [
  { value: 'business_card', label: 'Business' },
  { value: 'personal_card', label: 'Personal' },
];

const PAY_FROM = [
  { value: 'business_checking', label: 'Business Checking' },
  { value: 'personal_checking', label: 'Personal Checking' },
];

// ---------- small helpers (kept module-local; no math) -----------------
const rid = (p) => `${p}-${Math.random().toString(36).slice(2, 9)}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// ---------- empty defaults so "Add" yields sane skeletons --------------
const newAccount = () => ({
  id: rid('acc'),
  name: '',
  type: 'personal_checking',
  balance: 0,
  minimumTarget: 0,
  isDebt: false,
  debtPriority: 'business_card',
});
const newBill = () => ({
  id: rid('bill'),
  name: '',
  amount: 0,
  dueDate: todayISO(),
  payFrom: 'business_checking',
  required: true,
});
const newIncome = () => ({
  id: rid('inc'),
  name: '',
  amount: 0,
  expectedDate: todayISO(),
  depositTo: 'business_checking',
});
const newReserve = () => ({
  id: rid('res'),
  name: '',
  amount: 0,
  intendedFor: '',
  moveTo: 'business_checking',
});

// ---------- normaliser — UI strings → planner-shaped numbers -----------
function buildPayload(scenario) {
  return {
    planningDate: scenario.planningDate || todayISO(),
    majorIncomeDate: scenario.majorIncomeDate || null,
    accounts: (scenario.accounts || []).map((a) => {
      const isDebt = a.type === 'credit_card';
      return {
        id: a.id,
        name: a.name?.trim() || a.type,
        type: a.type,
        balance: num(a.balance),
        minimumTarget: isDebt ? 0 : num(a.minimumTarget),
        isDebt,
        debtPriority: isDebt ? a.debtPriority || 'business_card' : undefined,
      };
    }),
    bills: (scenario.bills || []).map((b) => ({
      id: b.id,
      name: b.name?.trim() || 'Bill',
      amount: num(b.amount),
      dueDate: b.dueDate || todayISO(),
      payFrom: b.payFrom,
      required: !!b.required,
    })),
    expectedIncome: (scenario.expectedIncome || []).map((i) => ({
      id: i.id,
      name: i.name?.trim() || 'Income',
      amount: num(i.amount),
      expectedDate: i.expectedDate || todayISO(),
      depositTo: i.depositTo,
    })),
    reserves: (scenario.reserves || []).map((r) => ({
      id: r.id,
      name: r.name?.trim() || 'Reserve',
      amount: num(r.amount),
      intendedFor: r.intendedFor?.trim() || '',
      moveTo: r.moveTo,
    })),
  };
}

// ---------- presentational sub-components (state lifted to parent) ------
function FieldRow({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 ' +
  'px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500';

function SectionShell({ title, helper, onAdd, children, testId }) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5" data-testid={testId}>
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          {helper && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{helper}</p>}
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
          data-testid={`${testId}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// ---------- main component ---------------------------------------------
function CashflowSettings() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [usingSample, setUsingSample] = useState(false);
  const [usingStarterTemplate, setUsingStarterTemplate] = useState(false);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Phase 2.2.1 — "Verify these balances when I save" checkbox state. Always
  // resets to false on render so the user opts in deliberately every time.
  const [verifyOnSave, setVerifyOnSave] = useState(false);

  // initial load — server returns personalized starter / Manny fallback when nothing saved
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/cashflow/scenario`);
        if (cancelled) return;
        setScenario(res.data.scenario);
        setHasSavedData(!!res.data.hasSavedData);
        setUsingSample(!!res.data.usingSample);
        setUsingStarterTemplate(!!res.data.usingStarterTemplate);
        setVerification(res.data.verification || null);
      } catch (err) {
        console.error('Cashflow scenario load failed:', err);
        toast.error('Could not load your cashflow scenario.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function patch(updates) {
    setScenario((s) => ({ ...s, ...updates }));
  }
  function patchListItem(listKey, idx, updates) {
    setScenario((s) => {
      const next = [...(s[listKey] || [])];
      next[idx] = { ...next[idx], ...updates };
      return { ...s, [listKey]: next };
    });
  }
  function removeListItem(listKey, idx) {
    setScenario((s) => {
      const next = [...(s[listKey] || [])];
      next.splice(idx, 1);
      return { ...s, [listKey]: next };
    });
  }
  function appendListItem(listKey, factory) {
    setScenario((s) => ({ ...s, [listKey]: [...(s[listKey] || []), factory()] }));
  }

  async function handleSave() {
    if (!scenario) return;
    setSaving(true);
    try {
      const payload = buildPayload(scenario);
      if (!payload.accounts.length) {
        toast.error('Add at least one account before saving.');
        setSaving(false);
        return;
      }
      // Only honour the verify-on-save flag when the editor is showing the
      // user's own data — starter and sample modes are projections / demo,
      // not bank-app-confirmed numbers (matches the Phase 2.2.1 spec).
      const allowVerifyOnSave = !usingStarterTemplate && !usingSample;
      const requestBody = allowVerifyOnSave && verifyOnSave
        ? { ...payload, verifyOnSave: true }
        : payload;
      const res = await axios.put(`${API}/cashflow/scenario`, requestBody);
      toast.success(
        allowVerifyOnSave && verifyOnSave
          ? 'Cashflow scenario saved and balances verified.'
          : 'Cashflow scenario saved.'
      );
      setHasSavedData(true);
      setUsingSample(false);
      setUsingStarterTemplate(false);
      setVerification(res?.data?.verification || null);
      // Reset the checkbox after save so the next save requires another
      // deliberate opt-in.
      setVerifyOnSave(false);
    } catch (err) {
      console.error('Cashflow scenario save failed:', err);
      toast.error(err?.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmVerified() {
    if (!hasSavedData) {
      toast.error('Save your scenario before confirming the balances.');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/cashflow/scenario/verify`);
      setVerification(res?.data?.verification || null);
      toast.success('Balances confirmed as accurate today.');
    } catch (err) {
      console.error('Cashflow verify failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not confirm balances. Please try again.');
    } finally {
      setVerifying(false);
    }
  }

  async function handleResetToStarter() {
    if (!window.confirm('Delete your saved cashflow scenario and reset to the starter template built from your PaperTrail accounts?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/cashflow/scenario`);
      const res = await axios.get(`${API}/cashflow/scenario`);
      setScenario(res.data.scenario);
      setHasSavedData(false);
      setUsingSample(!!res.data.usingSample);
      setUsingStarterTemplate(!!res.data.usingStarterTemplate);
      setVerification(res.data.verification || null);
      toast.success('Reset to your starter template.');
    } catch (err) {
      console.error('Cashflow scenario reset failed:', err);
      toast.error('Reset failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleLoadSample() {
    if (!window.confirm('Load the built-in sample (Manny) scenario into the editor? This does not save until you click Save scenario.')) return;
    setSaving(true);
    try {
      const res = await axios.get(`${API}/cashflow/sample-scenario`);
      setScenario(res.data);
      setHasSavedData(false);
      setUsingSample(true);
      setUsingStarterTemplate(false);
      setVerification({ state: 'sample', label: 'Demo', helper: 'Demo plan from the built-in sample scenario.', daysSince: null, verifiedAt: null, decisionReady: false });
      toast.success('Sample data loaded — review and Save to keep it.');
    } catch (err) {
      console.error('Cashflow sample load failed:', err);
      toast.error('Could not load the sample data.');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !scenario) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="cashflow-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-600 mb-1"
            data-testid="cashflow-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Cashflow Inputs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            These values feed the Cashflow Game Plan card. Math runs server-side — the AI only narrates the result.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleLoadSample}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            data-testid="cashflow-load-sample-btn"
            title="Load the built-in sample (Manny) scenario into the editor for inspection"
          >
            Load Sample Data
          </button>
          <button
            type="button"
            onClick={handleResetToStarter}
            disabled={saving || !hasSavedData}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            data-testid="cashflow-reset-btn"
            title={hasSavedData ? 'Delete your saved scenario and start over from the personalized starter built from your PaperTrail accounts' : 'No saved scenario to reset'}
          >
            <RotateCcw className="w-4 h-4" /> Reset to starter
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60"
            data-testid="cashflow-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save scenario
          </button>
        </div>
      </div>

      {/* ---- Phase 2.2.1: "Verify on save" checkbox ----------------------------
           Hidden in starter / sample modes per spec: only allow verify-on-save
           when the user is saving a real custom scenario.
      */}
      {!usingStarterTemplate && !usingSample && (
        <label
          className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer select-none"
          data-testid="cashflow-verify-on-save-row"
        >
          <input
            type="checkbox"
            checked={verifyOnSave}
            onChange={(e) => setVerifyOnSave(e.target.checked)}
            disabled={saving}
            className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
            data-testid="cashflow-verify-on-save-checkbox"
          />
          <span>
            <span className="font-medium">Verify these balances when I save.</span>{' '}
            <span className="opacity-75">
              Marks the scenario as verified today so the dashboard shows it as decision-ready in a single click.
            </span>
          </span>
        </label>
      )}

      {!hasSavedData && usingStarterTemplate && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2" data-testid="cashflow-starter-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-300" />
          <div>
            <strong>This plan is using projected starter balances.</strong> Update and save your real balances before relying on these recommendations.
            The starter was built from your PaperTrail projected balances and weekly income target — not from your bank app. Edit each field below to match your real bank balances, add upcoming bills, then click <strong>Save scenario</strong>.
          </div>
        </div>
      )}

      {usingSample && (
        <div className="rounded-md border border-amber-300/40 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="cashflow-sample-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            You are viewing the <strong>built-in sample (Manny) scenario</strong>. It is not saved to your account. Adjust the numbers below and click <strong>Save scenario</strong> to make it your own.
          </div>
        </div>
      )}

      {/* ---- Verification status card (saved scenarios only) ------------------ */}
      {hasSavedData && verification && (() => {
        const state = verification.state;
        const isVerified = state === 'verified';
        const isStale = state === 'stale';
        const Icon = isVerified ? ShieldCheck : ShieldAlert;
        const tone = isVerified
          ? 'border-emerald-300/50 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-100'
          : 'border-amber-300/50 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100';
        const iconTone = isVerified ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300';
        const headline = isVerified
          ? 'Balances verified'
          : isStale
            ? 'Verification is stale'
            : 'Balances not yet verified';
        return (
          <div className={`rounded-md border px-4 py-3 text-sm flex items-start gap-3 ${tone}`} data-testid="cashflow-verification-card" data-state={state}>
            <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconTone}`} />
            <div className="flex-1 space-y-2">
              <div>
                <p className="font-semibold" data-testid="cashflow-verification-headline">{headline}</p>
                <p className="text-xs opacity-90 mt-0.5" data-testid="cashflow-verification-helper">{verification.helper}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleConfirmVerified}
                  disabled={verifying}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-60 ${
                    isVerified
                      ? 'border border-emerald-400/60 text-emerald-800 dark:text-emerald-100 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  data-testid="cashflow-confirm-verified-btn"
                >
                  {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  {isVerified ? 'Re-confirm balances accurate today' : 'Confirm balances are accurate as of today'}
                </button>
                {!isVerified && (
                  <span className="text-[11px] opacity-75">
                    This is the trust signal that makes the dashboard plan decision-ready.
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---- Planning dates ------------------------------------------- */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="cashflow-section-dates">
        <FieldRow label="Planning date">
          <input
            type="date"
            className={inputCls}
            value={scenario.planningDate || ''}
            onChange={(e) => patch({ planningDate: e.target.value })}
            data-testid="cashflow-planning-date"
          />
        </FieldRow>
        <FieldRow label="Major income date (e.g. quarterly bonus, large deposit)">
          <input
            type="date"
            className={inputCls}
            value={scenario.majorIncomeDate || ''}
            onChange={(e) => patch({ majorIncomeDate: e.target.value || null })}
            data-testid="cashflow-major-income-date"
          />
        </FieldRow>
      </section>

      {/* ---- Accounts ------------------------------------------------- */}
      <SectionShell
        title="Accounts"
        helper="Cash accounts (checking + HYSA) and credit card debts. Set the target floor you want to protect on each cash account."
        onAdd={() => appendListItem('accounts', newAccount)}
        testId="cashflow-section-accounts"
      >
        {(scenario.accounts || []).map((a, idx) => (
          <div key={a.id} className="grid grid-cols-12 gap-2 items-end border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0">
            <div className="col-span-12 sm:col-span-3">
              <FieldRow label="Name">
                <input className={inputCls} value={a.name} onChange={(e) => patchListItem('accounts', idx, { name: e.target.value })} placeholder="e.g. Personal Checking" data-testid={`cashflow-account-name-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-12 sm:col-span-3">
              <FieldRow label="Type">
                <select className={inputCls} value={a.type} onChange={(e) => patchListItem('accounts', idx, { type: e.target.value })} data-testid={`cashflow-account-type-${idx}`}>
                  {ACCOUNT_TYPES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label={a.type === 'credit_card' ? 'Balance owed' : 'Current balance'}>
                <input type="number" step="0.01" className={inputCls} value={a.balance} onChange={(e) => patchListItem('accounts', idx, { balance: e.target.value })} data-testid={`cashflow-account-balance-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              {a.type === 'credit_card' ? (
                <FieldRow label="Debt category">
                  <select className={inputCls} value={a.debtPriority} onChange={(e) => patchListItem('accounts', idx, { debtPriority: e.target.value })} data-testid={`cashflow-account-debt-${idx}`}>
                    {DEBT_PRIORITIES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </FieldRow>
              ) : (
                <FieldRow label="Target floor">
                  <input type="number" step="0.01" className={inputCls} value={a.minimumTarget} onChange={(e) => patchListItem('accounts', idx, { minimumTarget: e.target.value })} data-testid={`cashflow-account-floor-${idx}`} />
                </FieldRow>
              )}
            </div>
            <div className="col-span-12 sm:col-span-2 flex justify-end">
              <button type="button" onClick={() => removeListItem('accounts', idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700" data-testid={`cashflow-account-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          </div>
        ))}
        {!(scenario.accounts || []).length && (
          <p className="text-sm text-gray-500">No accounts yet — click <strong>Add</strong> to add Personal, Business, HYSA, or a credit card.</p>
        )}
      </SectionShell>

      {/* ---- Bills ---------------------------------------------------- */}
      <SectionShell
        title="Upcoming bills"
        helper="Required bills are paid first. Mark non-essential bills as not required to keep them deprioritized."
        onAdd={() => appendListItem('bills', newBill)}
        testId="cashflow-section-bills"
      >
        {(scenario.bills || []).map((b, idx) => (
          <div key={b.id} className="grid grid-cols-12 gap-2 items-end border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0">
            <div className="col-span-12 sm:col-span-3">
              <FieldRow label="Name">
                <input className={inputCls} value={b.name} onChange={(e) => patchListItem('bills', idx, { name: e.target.value })} data-testid={`cashflow-bill-name-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label="Amount">
                <input type="number" step="0.01" className={inputCls} value={b.amount} onChange={(e) => patchListItem('bills', idx, { amount: e.target.value })} data-testid={`cashflow-bill-amount-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label="Due date">
                <input type="date" className={inputCls} value={b.dueDate} onChange={(e) => patchListItem('bills', idx, { dueDate: e.target.value })} data-testid={`cashflow-bill-due-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-8 sm:col-span-3">
              <FieldRow label="Pay from">
                <select className={inputCls} value={b.payFrom} onChange={(e) => patchListItem('bills', idx, { payFrom: e.target.value })} data-testid={`cashflow-bill-payfrom-${idx}`}>
                  {PAY_FROM.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FieldRow>
            </div>
            <div className="col-span-4 sm:col-span-1 flex items-center pb-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!b.required} onChange={(e) => patchListItem('bills', idx, { required: e.target.checked })} data-testid={`cashflow-bill-required-${idx}`} />
                Required
              </label>
            </div>
            <div className="col-span-12 sm:col-span-1 flex justify-end">
              <button type="button" onClick={() => removeListItem('bills', idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700" data-testid={`cashflow-bill-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </SectionShell>

      {/* ---- Expected income ----------------------------------------- */}
      <SectionShell
        title="Expected income"
        helper="Anything you expect to deposit before your next major income event. The largest deposit on or after the major income date is treated as the reset."
        onAdd={() => appendListItem('expectedIncome', newIncome)}
        testId="cashflow-section-income"
      >
        {(scenario.expectedIncome || []).map((i, idx) => (
          <div key={i.id} className="grid grid-cols-12 gap-2 items-end border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0">
            <div className="col-span-12 sm:col-span-4">
              <FieldRow label="Source">
                <input className={inputCls} value={i.name} onChange={(e) => patchListItem('expectedIncome', idx, { name: e.target.value })} data-testid={`cashflow-income-name-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label="Amount">
                <input type="number" step="0.01" className={inputCls} value={i.amount} onChange={(e) => patchListItem('expectedIncome', idx, { amount: e.target.value })} data-testid={`cashflow-income-amount-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label="Expected date">
                <input type="date" className={inputCls} value={i.expectedDate} onChange={(e) => patchListItem('expectedIncome', idx, { expectedDate: e.target.value })} data-testid={`cashflow-income-date-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-9 sm:col-span-3">
              <FieldRow label="Deposit to">
                <select className={inputCls} value={i.depositTo} onChange={(e) => patchListItem('expectedIncome', idx, { depositTo: e.target.value })} data-testid={`cashflow-income-deposit-${idx}`}>
                  {PAY_FROM.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FieldRow>
            </div>
            <div className="col-span-3 sm:col-span-1 flex justify-end">
              <button type="button" onClick={() => removeListItem('expectedIncome', idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700" data-testid={`cashflow-income-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </SectionShell>

      {/* ---- Reserves ------------------------------------------------- */}
      <SectionShell
        title="Reserves"
        helper="Money already earmarked for a specific bill (e.g. an Office Pack reserve). Moves before its bill is paid."
        onAdd={() => appendListItem('reserves', newReserve)}
        testId="cashflow-section-reserves"
      >
        {(scenario.reserves || []).map((r, idx) => (
          <div key={r.id} className="grid grid-cols-12 gap-2 items-end border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0">
            <div className="col-span-12 sm:col-span-4">
              <FieldRow label="Name">
                <input className={inputCls} value={r.name} onChange={(e) => patchListItem('reserves', idx, { name: e.target.value })} data-testid={`cashflow-reserve-name-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <FieldRow label="Amount">
                <input type="number" step="0.01" className={inputCls} value={r.amount} onChange={(e) => patchListItem('reserves', idx, { amount: e.target.value })} data-testid={`cashflow-reserve-amount-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-12 sm:col-span-3">
              <FieldRow label="Intended for">
                <input className={inputCls} value={r.intendedFor} onChange={(e) => patchListItem('reserves', idx, { intendedFor: e.target.value })} data-testid={`cashflow-reserve-intent-${idx}`} />
              </FieldRow>
            </div>
            <div className="col-span-9 sm:col-span-2">
              <FieldRow label="Move to">
                <select className={inputCls} value={r.moveTo} onChange={(e) => patchListItem('reserves', idx, { moveTo: e.target.value })} data-testid={`cashflow-reserve-moveto-${idx}`}>
                  {PAY_FROM.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FieldRow>
            </div>
            <div className="col-span-3 sm:col-span-1 flex justify-end">
              <button type="button" onClick={() => removeListItem('reserves', idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700" data-testid={`cashflow-reserve-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </SectionShell>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60"
          data-testid="cashflow-save-btn-bottom"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save scenario
        </button>
      </div>
    </div>
  );
}

export default CashflowSettings;
